/**
 * @fileoverview Related-party flag — shared type and wording.
 *
 * Purpose: the shape the creditors register and the POD claims schedule join
 * on, plus the sentence that explains a match, kept beside the badge that
 * renders it.
 */

/** Mirrors the match shape returned by `listRelatedPartyFlagsForJob`. */
export interface RelatedPartyMatchUI {
  partyId: string;
  legalName: string;
  relationshipCategory: string;
  relationshipStatus: "confirmed" | "probable" | "possible" | "excluded";
  matchedName: string;
  matchedOn: "legal" | "trading" | "linked_person";
}

const CATEGORY_LABELS: Record<string, string> = {
  director: "Director",
  former_director: "Former director",
  shareholder: "Shareholder",
  related_body_corporate: "Related body corporate",
  director_relative: "Director relative",
  spouse_of_director: "Spouse of director",
  controlled_entity: "Controlled entity",
  associate: "Associate",
  unknown_associated_party: "Unknown associated party",
  spouse: "Spouse (Bankruptcy Act s 5)",
  de_facto_partner: "De facto partner (s 5)",
  former_spouse: "Former spouse (s 5)",
  former_de_facto_partner: "Former de facto partner (s 5)",
  relative: "Relative (s 5)",
};

const STATUS_LABELS: Record<RelatedPartyMatchUI["relationshipStatus"], string> =
  {
    confirmed: "Confirmed",
    probable: "Probable",
    possible: "Possible",
    excluded: "Excluded",
  };

/**
 * Explains the match in a sentence — which party the creditor was matched to,
 * by which of their names, and how confident the register is about the
 * relationship.
 */
export function relatedPartyExplanation(match: RelatedPartyMatchUI): string {
  const category =
    CATEGORY_LABELS[match.relationshipCategory] ?? match.relationshipCategory;
  const status = STATUS_LABELS[match.relationshipStatus].toLowerCase();
  const via =
    match.matchedOn === "legal"
      ? ""
      : match.matchedOn === "trading"
        ? ` (trading as ${match.matchedName})`
        : ` (linked person ${match.matchedName})`;
  return `${match.legalName}${via} — ${category}, ${status} on the related parties register.`;
}
